import { Alert, Button, Card, Flex, Spin, Typography } from "antd";
import { VerticalSpacedDiv } from "components/divs";
import { useApiFetch } from "hooks/useApiFetch";
import { UserManager } from "oidc-client-ts";
import React, { useCallback, useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useLocation, useNavigate } from "react-router-dom";

import { getCurrentToken } from "../tokens/TokenProvider";

const OIDC_ORG_SLUG_KEY = "p0-oidc-orgslug";
export const OIDC_POPUP_SETTINGS_KEY = "p0-oidc-popup-settings";

export const CustomOidcCallbackScreen: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const error = auth.error;
  const hasNavigatedRef = useRef(false);

  useEffect(() => {
    if (hasNavigatedRef.current) {
      return;
    }

    if (!auth.isLoading && auth.isAuthenticated && !error) {
      hasNavigatedRef.current = true;

      const orgSlug = window.sessionStorage.getItem(OIDC_ORG_SLUG_KEY);
      if (orgSlug) {
        window.sessionStorage.removeItem(OIDC_ORG_SLUG_KEY);
      }

      navigate("/oidc/debug", { replace: true, state: { orgSlug } });
      return;
    }
  }, [auth.isLoading, auth.isAuthenticated, error, navigate, auth.user]);

  return (
    <Flex align="center" justify="center" style={{ height: "100vh" }}>
      {error ? (
        <Alert
          type="error"
          message="OIDC sign-in failed"
          description={error.message}
          showIcon
        />
      ) : (
        <Spin size="large" />
      )}
    </Flex>
  );
};

/**
 * TODO: ENG-6702 Remove once the custom OIDC flow is fully integrated
 */

type DebugResponse = {
  user?: {
    uid: string;
    email?: string;
    authTrail: {
      token: string;
      tokenId: string;
    };
  };
  claims?: Record<string, unknown>;
};

/**
 * ENG-6702: This is a temporary screen used to debug the custom OIDC flow
 * prior to the full integration of the custom OIDC flow.
 *
 * Once the custom OIDC flow is fully integrated, this screen should be removed
 * and the user will be redirected to their 'home' (dashboard or JIT)
 * page upon successful authentication.
 */
export const CustomOidcDebugScreen: React.FC = () => {
  const auth = useAuth();
  const { state } = useLocation();
  const [debugData, setDebugData] = React.useState<DebugResponse | null>(null);

  const orgSlug =
    state.orgSlug ?? window.sessionStorage.getItem(OIDC_ORG_SLUG_KEY);

  const apiFetch = useApiFetch();

  useEffect(() => {
    /**
     * Use debug endpoint to show that the JWT has
     * been successfully validated by the backend.
     */
    const fetchDebugData = async () => {
      const accessToken = await getCurrentToken();
      if (!orgSlug || !accessToken) return;

      apiFetch(`o/${orgSlug}/auth/v2/oidc/debug`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token: accessToken }),
      }).then(async (response) => {
        if (response) {
          const data = await response.json();
          setDebugData(data);
        }
      });
    };

    fetchDebugData();
  }, [apiFetch, orgSlug]);

  const handleBackToLogin = useCallback(async () => {
    window.sessionStorage.removeItem(OIDC_ORG_SLUG_KEY);
    await auth.removeUser();

    // Pass id_token_hint per OIDC RP-Initiated Logout spec
    // Helps the provider identify which user session to terminate
    const options = auth.user?.id_token
      ? { id_token_hint: auth.user.id_token }
      : undefined;

    await auth.signoutRedirect?.(options);
  }, [auth]);

  const displaySub = debugData?.user?.uid ?? "unknown";
  const displayClaims = debugData?.claims;

  const authTrail = debugData?.user?.authTrail;

  if (auth.isLoading) {
    return (
      <Flex align="center" justify="center" style={{ height: "100vh" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Flex align="center" justify="center" style={{ height: "100vh" }}>
        <Alert
          type="error"
          message="Not authenticated"
          description="OIDC session was not detected. Please sign in again."
          showIcon
        />
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" style={{ height: "100vh" }}>
      <Card title="OIDC Debug">
        <VerticalSpacedDiv style={{ gap: "1em" }}>
          <Flex vertical gap="small">
            <Typography.Text strong>Subject</Typography.Text>
            <Typography.Paragraph copyable style={{ marginBottom: 0 }}>
              {displaySub}
            </Typography.Paragraph>
          </Flex>
          {debugData ? (
            <>
              <Flex vertical gap="small">
                <Typography.Text strong>AuthTrail</Typography.Text>
                <pre style={{ fontSize: 11, overflow: "auto", maxHeight: 300 }}>
                  {JSON.stringify(authTrail, null, 2)}
                </pre>
              </Flex>
              <Flex vertical gap="small">
                <Typography.Text strong>Claims</Typography.Text>
                <pre style={{ fontSize: 11, overflow: "auto", maxHeight: 300 }}>
                  {JSON.stringify(displayClaims, null, 2)}
                </pre>
              </Flex>
            </>
          ) : (
            <Spin size="large" />
          )}
          <Button type="primary" onClick={handleBackToLogin}>
            Back to login
          </Button>
        </VerticalSpacedDiv>
      </Card>
    </Flex>
  );
};

/** Handles the redirect URI for popup-based OIDC sign-in flows.
 *  Mounted at /oidc/popup-callback. Reads the OIDC authority stored
 *  in sessionStorage by OidcSignInButton, then relays the authorization
 *  response back to the opener window via oidc-client-ts.
 */
export const OidcPopupCallbackScreen: React.FC = () => {
  useEffect(() => {
    const raw = sessionStorage.getItem(OIDC_POPUP_SETTINGS_KEY);
    const { authority, clientId } = raw ? JSON.parse(raw) : {};
    const popupRedirectUri = `${window.location.origin}/oidc/popup-callback`;
    new UserManager({
      authority: authority ?? "",
      client_id: clientId ?? "",
      redirect_uri: popupRedirectUri,
    })
      .signinPopupCallback()
      .catch(console.error);
  }, []);

  return (
    <Flex align="center" justify="center" style={{ height: "100vh" }}>
      <Spin size="large" />
    </Flex>
  );
};
